/**
 * 認證狀態 Store
 *
 * 管理使用者認證相關的狀態
 */

import { createStore } from './index';
import { AUTH } from '@/config/constants';
import type { User, AuthProvider } from '@/types';
import { encrypt, decrypt, safeJsonParse } from '@/utils/security';
import { createLogger } from '@/utils/logger';
import { TokenManager } from '@/utils/token-manager';
import { refreshCsrfToken, initCsrfToken, clearCsrfToken } from '@/utils/csrf';
import { resetPlannerState } from './planner-store';

// 建立具名 logger
const logger = createLogger('AuthStore');

// 加密金鑰（實際應用中應該從環境變數取得，但前端加密只是模糊化，真正安全需要後端 httpOnly cookies）
const STORAGE_ENCRYPTION_KEY = 'raiseplan-storage-key-2024';

// ========================================================================
// 狀態型別
// ========================================================================

export interface AuthState {
  /** 是否已認證 */
  isAuthenticated: boolean;

  /** 當前使用者資訊 */
  user: User | null;

  /** 認證 Token */
  token: string | null;

  /** 認證提供者 */
  provider: AuthProvider | null;

  /** 是否正在載入 */
  isLoading: boolean;

  /** 錯誤訊息 */
  error: string | null;
}

// ========================================================================
// 初始狀態
// ========================================================================

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  provider: null,
  isLoading: true, // 初始為 true，因為需要檢查 localStorage
  error: null,
};

// ========================================================================
// Store 實例
// ========================================================================

export const authStore = createStore<AuthState>(initialState);

// ========================================================================
// Actions
// ========================================================================

/**
 * 安全地存取 localStorage（處理追蹤防護等瀏覽器限制）
 *
 * @param key - localStorage key
 * @returns 值或 null（如果存取被阻止）
 */
function safeGetLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    // 當瀏覽器追蹤防護阻止存取時會拋出異常
    logger.warn('localStorage access blocked (possibly due to tracking prevention):', error);
    return null;
  }
}

/**
 * 安全地移除 localStorage 項目（處理追蹤防護等瀏覽器限制）
 *
 * @param key - localStorage key
 */
function safeRemoveLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // 當瀏覽器追蹤防護阻止存取時會拋出異常
    logger.warn('localStorage removal blocked:', error);
  }
}

/**
 * 從 localStorage 載入認證資料（加密版本）
 */
export async function loadAuthFromStorage(): Promise<void> {
  try {
    const encryptedToken = safeGetLocalStorage(AUTH.TOKEN_KEY);
    const encryptedUserJson = safeGetLocalStorage(AUTH.USER_KEY);

    // 如果沒有資料，直接結束載入狀態
    if (!encryptedToken || !encryptedUserJson) {
      authStore.setState({ isLoading: false });
      return;
    }

    // 驗證資料是否看起來像有效的加密資料（非空字串）
    if (encryptedToken.trim() === '' || encryptedUserJson.trim() === '') {
      logger.warn('Empty encrypted data found, clearing storage');
      safeRemoveLocalStorage(AUTH.TOKEN_KEY);
      safeRemoveLocalStorage(AUTH.USER_KEY);
      authStore.setState({ isLoading: false });
      return;
    }

    // 解密資料
    const token = await decrypt(encryptedToken, STORAGE_ENCRYPTION_KEY);
    const userJson = await decrypt(encryptedUserJson, STORAGE_ENCRYPTION_KEY);
    const user = safeJsonParse<User | null>(userJson, null);

    if (user) {
      authStore.setState({
        isAuthenticated: true,
        user,
        token,
        provider: user.provider,
        isLoading: false,
        error: null,
      });
    } else {
      authStore.setState({ isLoading: false });
    }
  } catch (error) {
    logger.error('Failed to load auth from storage:', error);
    // 如果解密失敗，清除舊資料
    safeRemoveLocalStorage(AUTH.TOKEN_KEY);
    safeRemoveLocalStorage(AUTH.USER_KEY);
    // 不顯示錯誤訊息給用戶，只是讓他們重新登入
    authStore.setState({ isLoading: false });
  }
}

/**
 * 安全地設定 localStorage 項目（處理追蹤防護等瀏覽器限制）
 *
 * @param key - localStorage key
 * @param value - 要儲存的值
 * @returns 是否成功
 */
function safeSetLocalStorage(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    // 當瀏覽器追蹤防護阻止存取時會拋出異常
    logger.warn('localStorage write blocked (possibly due to tracking prevention):', error);
    return false;
  }
}

/**
 * 設定認證成功的狀態（加密版本）
 * 🔒 安全性：設定 Token 過期時間追蹤 + 刷新 CSRF Token
 */
export async function setAuthenticated(user: User, token: string): Promise<void> {
  try {
    // 加密後儲存到 localStorage
    const encryptedToken = await encrypt(token, STORAGE_ENCRYPTION_KEY);
    const encryptedUser = await encrypt(JSON.stringify(user), STORAGE_ENCRYPTION_KEY);

    // 嘗試儲存，即使失敗也繼續（用戶仍可登入，只是重整後需要重新登入）
    const tokenSaved = safeSetLocalStorage(AUTH.TOKEN_KEY, encryptedToken);
    const userSaved = safeSetLocalStorage(AUTH.USER_KEY, encryptedUser);

    if (!tokenSaved || !userSaved) {
      logger.warn('Auth data could not be persisted - user will need to re-login after refresh');
    }

    // ✅ 設定 Token 過期時間（24 小時）
    TokenManager.setTokenExpiry();

    // ✅ 刷新 CSRF Token 防止 Session Fixation 攻擊
    refreshCsrfToken();

    authStore.setState({
      isAuthenticated: true,
      user,
      token,
      provider: user.provider,
      isLoading: false,
      error: null,
    });

    logger.info('Authentication set successfully', {
      provider: user.provider,
      tokenExpiry: TokenManager.formatTimeUntilExpiry(),
      csrfTokenRefreshed: true
    });
  } catch (error) {
    logger.error('Failed to encrypt auth data:', error);
    throw new Error('無法儲存認證資料');
  }
}

/**
 * 設定認證載入中狀態
 */
export function setAuthLoading(isLoading: boolean): void {
  authStore.setState({ isLoading, error: null });
}

/**
 * 設定認證錯誤
 */
export function setAuthError(error: string): void {
  authStore.setState({ isLoading: false, error });
}

/**
 * 登出
 * 🔒 安全性：清除 Token 過期時間 + 刷新 CSRF Token + 清除規劃狀態
 */
export function logout(): void {
  // 清除 localStorage（使用安全版本處理追蹤防護）
  safeRemoveLocalStorage(AUTH.TOKEN_KEY);
  safeRemoveLocalStorage(AUTH.USER_KEY);

  // ✅ 清除 Token 過期時間
  TokenManager.clearTokenExpiry();

  // ✅ 清理並重新生成 CSRF Token（防止 Token 殘留）
  clearCsrfToken();
  initCsrfToken();

  // ✅ 清除規劃狀態（避免不同用戶間資料洩漏）
  resetPlannerState();

  // 重設狀態
  authStore.setState({
    isAuthenticated: false,
    user: null,
    token: null,
    provider: null,
    isLoading: false,
    error: null,
  });

  logger.info('User logged out successfully', {
    csrfTokenRefreshed: true,
    plannerStateReset: true
  });
}

/**
 * 取得當前 Token（用於 API 呼叫）
 */
export function getAuthToken(): string | null {
  return authStore.getState().token;
}

/**
 * 檢查是否已認證
 */
export function isAuthenticated(): boolean {
  return authStore.getState().isAuthenticated;
}

// ========================================================================
// 簡化 Actions（元件用）
// ========================================================================

/**
 * 設定使用者（簡化版，用於元件）
 */
export function setUser(user: User): void {
  authStore.setState({
    isAuthenticated: true,
    user,
    isLoading: false,
    error: null,
  });
}

/**
 * 設定錯誤訊息（簡化版，用於元件）
 */
export function setError(error: string): void {
  setAuthError(error);
}

/**
 * 清除錯誤訊息
 */
export function clearError(): void {
  authStore.setState({ error: null });
}
